import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import EventWatermark from '../components/EventWatermark';
import EventGrid from '../components/events/EventGrid';
import EventGridSkeleton from '../components/events/EventGridSkeleton';
import { getEventLocationShort, getFeaturedHeroLocation, getFeaturedHeroBrief, getEventShortAbsoluteUrl } from '../utils/eventFormat';
import { getEventImageUrl, DEFAULT_EVENT_IMAGE } from '../utils/mediaUrl';
import { API_BASE, EVENTS_BATCH_SIZE } from '../constants/eventGrid';
import { useInfiniteScrollTrigger } from '../hooks/useInfiniteScrollTrigger';
import BrandLogo from '../components/brand/BrandLogo';
import { HERO_GRADIENT, BRAND_NAME } from '../constants/brand';

const HomePage = () => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const dropdownPanelRef = useRef(null);
  const [searchDropdownPosition, setSearchDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isTransitioningRef = useRef(isTransitioning);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [carouselPosition, setCarouselPosition] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [visibleTagsCount, setVisibleTagsCount] = useState(3);
  const [isAutoScrollActive, setIsAutoScrollActive] = useState(false);
  const [currentCycle, setCurrentCycle] = useState(0);

  // Keep dropdown aligned to the input using viewport coordinates
  const rafId = React.useRef();
  const updateDropdownDOMPosition = () => {
    if (!searchInputRef.current || !dropdownPanelRef.current) return;
    const rect = searchInputRef.current.getBoundingClientRect();
    dropdownPanelRef.current.style.top = `${rect.bottom + 8}px`;
    dropdownPanelRef.current.style.left = `${rect.left}px`;
    dropdownPanelRef.current.style.width = `${rect.width}px`;
  };

  useEffect(() => {
    if (!showSuggestions) return;
    updateDropdownDOMPosition();
    let rafId = null;
    const handleFrame = () => {
      updateDropdownDOMPosition();
      rafId = requestAnimationFrame(handleFrame);
    };
    window.addEventListener('scroll', handleFrame, true);
    window.addEventListener('resize', handleFrame);
    // kick off the RAF loop
    rafId = requestAnimationFrame(handleFrame);
    return () => {
      window.removeEventListener('scroll', handleFrame, true);
      window.removeEventListener('resize', handleFrame);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [showSuggestions]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/categories`);
        const data = await response.json();
        
        if (data.success) {
          setCategories(data.data.categories || []);
        } else {
          setCategories([]);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  // Fetch featured events
  useEffect(() => {
    const fetchFeaturedEvents = async () => {
      try {
        setFeaturedLoading(true);
        const response = await fetch(`${API_BASE}/api/events/featured`);
        const data = await response.json();

        if (data.success) {
          setFeaturedEvents(data.data.events || []);
        } else {
          setFeaturedEvents([]);
        }
      } catch (error) {
        console.error('Error fetching featured events:', error);
        setFeaturedEvents([]);
      } finally {
        setFeaturedLoading(false);
      }
    };

    fetchFeaturedEvents();
  }, []);

  const fetchUpcomingPage = useCallback(async (page, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setEventsLoading(true);
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/events?page=${page}&limit=${EVENTS_BATCH_SIZE}&sortBy=date&sortOrder=asc`
      );
      const data = await response.json();

      if (data.success) {
        const batch = data.data.events || [];
        setUpcomingEvents((prev) => (append ? [...prev, ...batch] : batch));
        setHasMoreEvents(!!data.data.pagination?.hasNextPage);
        setEventsPage(page);
      } else if (!append) {
        setUpcomingEvents([]);
        setHasMoreEvents(false);
      }
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      if (!append) {
        setUpcomingEvents([]);
        setHasMoreEvents(false);
      }
    } finally {
      setEventsLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchUpcomingPage(1, false);
  }, [fetchUpcomingPage]);

  const loadMoreUpcoming = useCallback(() => {
    if (eventsLoading || loadingMore || !hasMoreEvents) return;
    fetchUpcomingPage(eventsPage + 1, true);
  }, [eventsLoading, loadingMore, hasMoreEvents, eventsPage, fetchUpcomingPage]);

  const upcomingSentinelRef = useInfiniteScrollTrigger({
    onLoadMore: loadMoreUpcoming,
    hasMore: hasMoreEvents,
    loading: eventsLoading || loadingMore
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFeaturedDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(parseFloat(price));
  };

  const calculateDaysUntil = (eventDate) => {
    const now = new Date();
    const event = new Date(eventDate);
    const diffTime = event - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return '1 day left';
    return `${diffDays} days left`;
  };


  // Search suggestions functionality
  useEffect(() => {
    if (searchQuery.length > 2) {
      const suggestions = upcomingEvents.filter(event =>
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (event.venue?.name && event.venue.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (event.venue?.city && event.venue.city.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (event.category?.name && event.category.name.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 5); // Limit to 5 suggestions
      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
      
      // Update dropdown position when suggestions change
      if (searchInputRef.current) {
        const rect = searchInputRef.current.getBoundingClientRect();
        setSearchDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, upcomingEvents]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setShowSuggestions(false);
  };


  const handleSuggestionClick = (event) => {
    setSearchQuery(event.title);
    setShowSuggestions(false);
    // Navigate to event details page
    navigate(`/events/${event.id}`);
  };

  // Carousel functionality
  const nextEvent = () => {
    if (isTransitioningRef.current || featuredEvents.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
    setCurrentEventIndex((prev) => (prev + 1) % featuredEvents.length);
      setTimeout(() => setIsTransitioning(false), 100);
    }, 300);
  };

  const prevEvent = () => {
    if (isTransitioningRef.current || featuredEvents.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
    setCurrentEventIndex((prev) => (prev - 1 + featuredEvents.length) % featuredEvents.length);
      setTimeout(() => setIsTransitioning(false), 100);
    }, 300);
  };

  const goToEvent = (index) => {
    if (isTransitioningRef.current || index === currentEventIndex || featuredEvents.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentEventIndex(index);
      setTimeout(() => setIsTransitioning(false), 100);
    }, 300);
  };

  const handleCarouselSwipe = (direction) => {
    if (direction === 'left') {
      nextEvent();
    } else if (direction === 'right') {
      prevEvent();
    }
  };

  // Featured events auto-cycle (shorter, consistent on-screen time)
  useEffect(() => {
    if (featuredEvents.length <= 1) return;

    const FEATURED_INITIAL_DELAY_MS = 4200;
    const FEATURED_CYCLE_MS = 4200;

    let intervalId = null;
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        nextEvent();
      }, FEATURED_CYCLE_MS);
    }, FEATURED_INITIAL_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
    // Only re-run when the number of featured events changes.
  }, [featuredEvents.length]);

  // Mouse drag functionality for carousel
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [touchStart, setTouchStart] = useState(0);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const dragDistance = e.clientX - dragStart;
    if (Math.abs(dragDistance) > 50) {
      if (dragDistance > 0) {
        prevEvent();
      } else {
        nextEvent();
      }
      setIsDragging(false);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch support for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const touchDistance = touchStart - touchEnd;
    
    if (Math.abs(touchDistance) > 50) {
      if (touchDistance > 0) {
        nextEvent();
      } else {
        prevEvent();
      }
    }
    setTouchStart(0);
  };

  const currentEvent = featuredEvents[currentEventIndex];

  // Share functionality
  const handleShare = async (event) => {
    const eventUrl = getEventShortAbsoluteUrl(event.id);
    setShareUrl(eventUrl);
    setShowShareModal(true);
  };

  const handleSocialShare = (platform, event) => {
    const url = getEventShortAbsoluteUrl(event.id);
    const title = event?.title || 'Check out this event!';
    const text = `Check out this amazing event: ${title}`;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareModal(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Event link copied to clipboard!');
      setShowShareModal(false);
    } catch (error) {
      console.log('Error copying to clipboard:', error);
    }
  };

  // Continuous auto-cycling tags (pages of categories from API)
  useEffect(() => {
    const pageSize = 6;
    const pages = Math.max(1, Math.ceil(categories.length / pageSize) || 1);
    const interval = setInterval(() => {
      setCurrentCycle((prev) => (prev + 1) % pages);
    }, 4000);

    return () => clearInterval(interval);
  }, [categories.length]);

  // Keep a ref in sync so the featured auto-cycle doesn't use stale state.
  useEffect(() => {
    isTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  // Calculate how many tags can fit based on screen width
  const calculateVisibleTags = () => {
    if (typeof window === 'undefined') return 6;
    
    const screenWidth = window.innerWidth;
    const containerPadding = 32; // px-4 on both sides
    const tagWidth = 120; // Approximate width of each tag including gap
    const availableWidth = screenWidth - containerPadding;
    const maxTags = Math.floor(availableWidth / tagWidth);
    
    return Math.max(3, Math.min(6, maxTags)); // At least 3, at most 6
  };

  // Show tags that fit on screen when cycle changes
  useEffect(() => {
    const fittingTags = calculateVisibleTags();
    setIsAutoScrollActive(true);
    setVisibleTagsCount(fittingTags); // Show only tags that fit

    // Stop animation after brief delay
    const stopAnimationTimeout = setTimeout(() => {
      setIsAutoScrollActive(false);
    }, 300); // Brief delay to let animation complete

    return () => clearTimeout(stopAnimationTimeout);
  }, [currentCycle]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      const fittingTags = calculateVisibleTags();
      setVisibleTagsCount(fittingTags);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categoryTagList = useMemo(
    () =>
      categories.map((c) => ({
        id: c.id,
        label: c.name,
        query: c.name,
      })),
    [categories]
  );

  const getCurrentTagSet = () => {
    if (categoryTagList.length === 0) return [];
    const pageSize = 6;
    const pages = Math.max(1, Math.ceil(categoryTagList.length / pageSize));
    const safeCycle = currentCycle % pages;
    return categoryTagList.slice(safeCycle * pageSize, safeCycle * pageSize + pageSize);
  };

  const categoryTags = getCurrentTagSet();

  return (
    <SiteLayout className="bg-orange-50">
      <Header />

      <SiteMain>
      {/* Hero Section with Featured Events Carousel */}
      <section className="relative py-8 sm:py-10 md:py-16 lg:py-20 text-white overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(209, 91, 45, 0.9) 0%, rgba(242, 201, 76, 0.8) 100%), url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23fff\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        zIndex: 100
      }}>
        {/* Floating elements */}
        <div className="absolute top-1/4 left-10 w-16 h-16 rounded-full bg-yellow-400 opacity-20 animate-bounce"></div>
        <div className="absolute top-1/3 right-16 w-24 h-24 rounded-full bg-white opacity-10 animate-bounce" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-1/4 left-1/4 w-20 h-20 rounded-full bg-orange-500 opacity-15 animate-bounce" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 right-10 w-12 h-12 rounded-full bg-yellow-300 opacity-15 animate-bounce" style={{animationDelay: '0.5s'}}></div>
        <div className="absolute bottom-1/3 right-1/4 w-14 h-14 rounded-full bg-yellow-500 opacity-10 animate-bounce" style={{animationDelay: '1.5s'}}></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-1/2 mb-8 lg:mb-0 lg:pr-6">
              <div className="mb-4">
                <span className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 bg-opacity-25 text-white px-3 py-1.5 rounded-md text-xs font-semibold mb-3 shadow-lg backdrop-blur-sm">
                  ✨ Kenya's Premier Event Platform
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight font-bold">
                Discover <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 via-yellow-400 to-white drop-shadow-lg">Authentic</span><br />
                <span className="text-3xl md:text-4xl lg:text-5xl">African Events</span>
              </h1>
              <p className="text-lg md:text-xl mb-8 max-w-2xl font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                From music festivals to cultural celebrations, find events that speak to your soul across Kenya and beyond.
              </p>
              
              <div className="w-full max-w-2xl relative mb-6" style={{ zIndex: 99999 }}>
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search for events, artists, or venues…" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => {
                      if (searchQuery.length > 2) {
                        setShowSuggestions(true);
                        updateDropdownDOMPosition();
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="w-full px-4 md:px-6 py-3 pr-16 md:pr-20 rounded-md bg-white bg-opacity-95 text-gray-800 shadow-2xl focus:outline-none focus:ring-4 focus:ring-yellow-500 focus:ring-opacity-30 text-base md:text-sm font-medium backdrop-blur-sm" 
                    style={{ fontSize: '16px', position: 'relative', zIndex: 99999 }}
                  />
                  
                  {/* Clear button - only show when there's text */}
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute top-1/2 -translate-y-1/2 bg-gray-300 hover:bg-gray-400 text-gray-600 hover:text-gray-800 w-6 h-6 rounded-md flex items-center justify-center transition-all duration-300 hover:scale-110"
                      style={{ right: '3rem', zIndex: 99999 }}
                      title="Clear search"
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  )}
                  
                  {/* Search button */}
                  <button 
                    onClick={() => handleSearch(searchQuery)}
                    className="absolute top-1/2 -translate-y-1/2 bg-gradient-to-r from-orange-600 to-orange-700 text-white px-3 py-1.5 rounded-md hover:from-orange-700 hover:to-orange-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                    style={{ right: '0rem', zIndex: 99999 }}
                  >
                    <i className="fas fa-search text-lg"></i>
                  </button>
                </div>
              </div>

              {/* Search Suggestions Dropdown via Portal (fixed to viewport, follows input) */}
              {showSuggestions && searchSuggestions.length > 0 && ReactDOM.createPortal(
                (
                  <div
                    ref={dropdownPanelRef}
                    className="fixed bg-white rounded-lg shadow-2xl max-h-80 overflow-y-auto border border-gray-200"
                    style={{
                      zIndex: 999999,
                      maxWidth: '680px',
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {searchSuggestions.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => handleSuggestionClick(event)}
                        className="p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-200"
                      >
                        <div className="flex items-center space-x-4">
                          <img
                            src={getEventImageUrl(event.imageUrl)}
                            alt={event.title}
                            onError={(e) => { e.currentTarget.src = DEFAULT_EVENT_IMAGE; }}
                            className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">{event.title}</h4>
                            <p className="text-gray-600 text-xs truncate">{getEventLocationShort(event)}</p>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-red-500 text-xs font-medium">{formatPrice(event.price)}</p>
                              <span className="bg-red-50 text-red-600 text-xs px-2 py-1 rounded-md font-medium">
                                {event.category?.name}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ),
                document.body
              )}

              {categoryTags.length > 0 && (
                <div className="flex gap-2 pb-2 justify-start">
                  {categoryTags.slice(0, visibleTagsCount).map((tag, index) => (
                    <button
                      key={`${currentCycle}-${tag.id}`}
                      onClick={() => setSearchQuery(tag.query)}
                      className="text-xs bg-white bg-opacity-20 text-white rounded-md px-3 py-1.5 hover:bg-opacity-40 transition-all duration-500 backdrop-blur-sm hover:scale-105 font-medium shadow-lg whitespace-nowrap"
                      style={{
                        animationDelay: isAutoScrollActive ? `${index * 50}ms` : '0ms',
                        animation: isAutoScrollActive ? 'fadeInUp 0.4s ease-out forwards' : 'none',
                      }}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:w-1/2 relative mt-4 sm:mt-0 lg:pl-4 w-full px-1 sm:px-0">
              {/* Featured Events Carousel */}
              {featuredLoading ? (
                <div className="rounded-xl overflow-hidden animate-pulse w-full max-w-xl md:max-w-2xl mx-auto lg:mx-0 lg:ml-auto bg-white/90 border border-white/40">
                  <div className="flex flex-col sm:flex-row sm:items-stretch sm:h-[272px]">
                    <div className="w-full aspect-[4/3] sm:aspect-auto sm:w-52 sm:flex-shrink-0 bg-gray-200" />
                    <div className="flex-1 flex flex-col p-3.5 sm:pl-5 sm:pr-11 sm:pt-3.5 sm:pb-2.5 min-w-0 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-4/5" />
                      <div className="h-3.5 bg-gray-200 rounded w-full" />
                      <div className="h-3.5 bg-gray-200 rounded w-3/5" />
                      <div className="h-8 bg-gray-200 rounded w-2/5 mt-auto" />
                    </div>
                  </div>
                </div>
              ) : featuredEvents.length > 0 ? (
                <div className="overflow-hidden text-gray-800 relative featured-event-card no-horizontal-scroll rounded-xl w-full max-w-xl md:max-w-2xl mx-auto lg:mx-0 lg:ml-auto bg-white border border-white/50 shadow-lg">
                  {(featuredEvents[currentEventIndex]?.status === 'SOLD_OUT' || featuredEvents[currentEventIndex]?.status === 'CANCELLED') ? (
                    <div
                      className={`featured-ribbon absolute z-40 flex items-center rounded-br-md border-b border-r shadow-sm ${
                        featuredEvents[currentEventIndex]?.status === 'SOLD_OUT'
                          ? 'bg-red-500 text-white border-red-600/30'
                          : 'bg-gray-500 text-white border-gray-600/30'
                      }`}
                    >
                      <span className="text-xs font-bold px-3 py-1.5 leading-none">
                        {featuredEvents[currentEventIndex]?.status === 'SOLD_OUT' ? 'Sold out' : 'Cancelled'}
                      </span>
                    </div>
                  ) : (
                    <div className="featured-ribbon absolute z-40 flex items-center gap-1.5 bg-white text-orange-600 rounded-br-md border-b border-r border-orange-200 shadow-sm">
                      <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 leading-none">
                        <i className="fas fa-star text-[10px]" />
                        Featured
                      </span>
                    </div>
                  )}

                  <div
                    className={`flex flex-col sm:flex-row sm:items-stretch sm:h-[272px] featured-transition ${
                      isTransitioning ? 'opacity-0' : 'opacity-100'
                    }`}
                  >
                    <div className="relative w-full aspect-[4/3] sm:aspect-auto sm:w-52 sm:flex-shrink-0 sm:h-full overflow-hidden bg-gray-100">
                      <Link to={`/events/${featuredEvents[currentEventIndex]?.id}`} className="block absolute inset-0">
                        <img
                          src={getEventImageUrl(featuredEvents[currentEventIndex]?.imageUrl)}
                          alt={featuredEvents[currentEventIndex]?.title}
                          onError={(e) => { e.currentTarget.src = DEFAULT_EVENT_IMAGE; }}
                          className="w-full h-full object-cover object-center"
                        />
                      </Link>
                    </div>

                    <div className="flex-1 min-w-0 self-stretch p-3.5 sm:pl-5 sm:pr-11 sm:pt-3.5 sm:pb-2 flex flex-col bg-white overflow-hidden">
                      <div className="shrink-0">
                        <div className="flex items-start sm:items-center gap-2 min-w-0 sm:h-7 mb-1.5">
                          <Link to={`/events/${featuredEvents[currentEventIndex]?.id}`} className="group min-w-0 flex-1 overflow-hidden">
                            <h2 className="text-base font-bold text-gray-900 line-clamp-2 sm:truncate sm:leading-7 group-hover:text-orange-600 transition-colors">
                              {featuredEvents[currentEventIndex]?.title}
                            </h2>
                          </Link>
                          {featuredEvents[currentEventIndex]?.status !== 'SOLD_OUT' &&
                            featuredEvents[currentEventIndex]?.status !== 'CANCELLED' && (
                            <span className="shrink-0 text-[10px] sm:text-xs font-medium text-orange-600 bg-orange-50 border border-orange-100 px-2 py-0.5 rounded-md whitespace-nowrap mt-0.5 sm:mt-0">
                              {calculateDaysUntil(featuredEvents[currentEventIndex]?.date)}
                            </span>
                          )}
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center gap-2 min-w-0 text-xs sm:text-sm text-gray-600 sm:h-5">
                            <i className="fas fa-calendar-alt text-orange-500 text-[11px] w-3.5 text-center shrink-0" />
                            <span className="truncate min-w-0">{formatFeaturedDate(featuredEvents[currentEventIndex]?.date)}</span>
                          </div>
                          <div className="flex items-center gap-2 min-w-0 text-xs sm:text-sm text-gray-600 sm:h-5">
                            <i className="fas fa-map-marker-alt text-orange-500 text-[11px] w-3.5 text-center shrink-0" />
                            <span className="truncate min-w-0">{getFeaturedHeroLocation(featuredEvents[currentEventIndex])}</span>
                          </div>
                        </div>
                      </div>

                      <div className="hidden sm:flex flex-1 items-center min-h-0 py-1">
                        <p className="w-full text-xs text-gray-500 leading-[1.4] line-clamp-2 overflow-hidden">
                          {getFeaturedHeroBrief(featuredEvents[currentEventIndex])}
                        </p>
                      </div>

                      <div className="shrink-0 pt-2.5 sm:pt-2 mt-auto border-t border-gray-100">
                        <div className="flex items-center justify-between gap-3 sm:h-10">
                          <div className="min-w-0 overflow-hidden">
                            <span className="text-[10px] sm:text-[11px] text-gray-400 uppercase tracking-wide leading-none">From</span>
                            <div className="text-sm sm:text-base font-bold text-gray-900 truncate leading-tight">
                              {formatPrice(featuredEvents[currentEventIndex]?.price)}
                            </div>
                          </div>
                          {featuredEvents[currentEventIndex]?.status === 'CANCELLED' ? (
                            <span className="shrink-0 text-xs font-semibold text-gray-400 px-3 py-1.5 rounded-md bg-gray-100 whitespace-nowrap">
                              Cancelled
                            </span>
                          ) : featuredEvents[currentEventIndex]?.status === 'SOLD_OUT' ? (
                            <Link
                              to={`/events/${featuredEvents[currentEventIndex]?.id}`}
                              className="shrink-0 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded-md transition-colors whitespace-nowrap"
                            >
                              View
                            </Link>
                          ) : (
                            <Link
                              to={`/events/${featuredEvents[currentEventIndex]?.id}`}
                              className="shrink-0 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 px-3.5 py-1.5 rounded-md transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
                            >
                              <i className="fas fa-ticket-alt text-[11px]" />
                              Get tickets
                            </Link>
                          )}
                        </div>

                        {featuredEvents.length > 1 && (
                          <div className="flex justify-center items-center pt-2 sm:pt-1.5 pb-0.5 gap-1.5">
                            {featuredEvents.map((_, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => goToEvent(index)}
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                  index === currentEventIndex
                                    ? 'bg-orange-500 w-5'
                                    : 'bg-gray-300 w-1.5 hover:bg-gray-400'
                                }`}
                                aria-label={`Go to event ${index + 1}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {featuredEvents.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={prevEvent}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-600 hover:text-orange-600 p-2 rounded-md shadow-md transition-all z-20 border border-gray-100"
                        aria-label="Previous event"
                      >
                        <i className="fas fa-chevron-left text-sm" />
                      </button>
                      <button
                        type="button"
                        onClick={nextEvent}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-600 hover:text-orange-600 p-2 rounded-md shadow-md transition-all z-20 border border-gray-100"
                        aria-label="Next event"
                      >
                        <i className="fas fa-chevron-right text-sm" />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="rounded-xl p-6 text-center w-full max-w-xl md:max-w-2xl mx-auto lg:mx-0 lg:ml-auto bg-white border border-white/50 shadow-lg">
                  <div className="text-4xl mb-3">🎪</div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">No featured events</h3>
                  <p className="text-sm text-gray-600 mb-4">Check back soon for amazing events!</p>
                  <Link to="/events" className="inline-block bg-terracotta text-white px-5 py-2 rounded-md text-sm font-semibold hover:bg-safari-gold transition-colors">
                    Browse all events
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="py-12 md:py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-orange-500/90 mb-1.5">
                Fresh vibes
              </p>
              <h2 className="text-xl md:text-2xl font-medium text-gray-800 tracking-tight">
                Upcoming vibes
              </h2>
            </div>
            <Link
              to="/events"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-300 bg-white hover:bg-orange-50 px-4 py-2 rounded-md transition-colors shrink-0"
            >
              View all
              <i className="fas fa-arrow-right text-[10px]" />
            </Link>
          </div>

          {eventsLoading ? (
            <EventGridSkeleton />
          ) : upcomingEvents.length > 0 ? (
            <EventGrid
              events={upcomingEvents}
              sentinelRef={upcomingSentinelRef}
              hasMore={hasMoreEvents}
              loadingMore={loadingMore}
            />
          ) : (
            <div className="text-center py-12 rounded-md border border-dashed border-gray-200 bg-gray-50/50">
              <p className="text-sm font-medium text-gray-900 mb-1">No upcoming events</p>
              <p className="text-sm text-gray-500 mb-5">Check back soon for new listings.</p>
              <Link
                to="/events"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-300 bg-white hover:bg-orange-50 px-4 py-2 rounded-md transition-colors"
              >
                Browse all events
                <i className="fas fa-arrow-right text-[10px]" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Intro - white ground, separate from pillars */}
      <section className="relative bg-white">
        <div className="container mx-auto px-4 pt-14 md:pt-16 lg:pt-20 pb-12 md:pb-14">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-6xl mx-auto">
            <div>
              <div className="flex items-center gap-2.5 mb-5">
                <BrandLogo link={false} size="sm" imgClassName="rounded-md" />
                <span className="text-sm font-medium text-gray-600 tracking-wide">
                  For every gathering
                </span>
              </div>
              <h2 className="text-xl md:text-2xl font-medium text-gray-900 leading-snug tracking-tight mb-4">
                Where the vibe meets the ticket
              </h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-lg">
                From rooftop sets and stadium nights to corporate summits and online masterclasses
                for entrepreneurs - list it, sell it on M-Pesa, and get guests in with a code that
                works at the door or on the stream.
              </p>
            </div>

            <div className="hidden lg:flex justify-end">
              <div className="w-full max-w-sm rounded-2xl bg-gray-100 p-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
                    <i className="fas fa-ticket-alt text-xl text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 leading-snug mb-2">
                      Live · Corporate · Online
                    </p>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Music, sports, culture, festivals, boardrooms, and entrepreneur webinars -
                      one ticket flow for every format.
                    </p>
                  </div>
                </div>
                <div className="mt-6 pt-5 border-t border-gray-200/90 flex flex-wrap gap-2">
                  <span className="text-[11px] font-semibold text-white bg-orange-500 px-2.5 py-1 rounded-md">
                    Nightlife
                  </span>
                  <span className="text-[11px] font-semibold text-white bg-stone-700 px-2.5 py-1 rounded-md">
                    Summits
                  </span>
                  <span className="text-[11px] font-semibold text-amber-950 bg-amber-300 px-2.5 py-1 rounded-md">
                    Masterclasses
                  </span>
                  <span className="text-[11px] font-semibold text-white bg-rose-500 px-2.5 py-1 rounded-md">
                    Community
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars - distinct band, soft wash into CTA */}
      <section className="relative overflow-hidden bg-gradient-to-b from-amber-50 via-orange-50 to-orange-100/90">
        <div className="container mx-auto px-4 py-12 md:py-14 relative z-10">
          <div className="grid md:grid-cols-3 gap-6 md:gap-5 max-w-6xl mx-auto">
            {[
              {
                icon: 'fa-bolt',
                title: 'Ready before doors open',
                body: 'Payouts clear ahead of showtime for live nights and webinars alike. No chasing after.'
              },
              {
                icon: 'fa-sliders-h',
                title: 'Run the whole show',
                body: 'Early birds, packages, group deals for live nights or corporate seats. Set the offer, watch bookings move.'
              },
              {
                icon: 'fa-qrcode',
                title: 'Real tickets, real access',
                body: 'Unique codes that scan clean at the door or unlock online. Screenshots and fakes stay out.'
              }
            ].map((item) => (
              <div
                key={item.title}
                className="text-left rounded-xl border border-orange-100/70 bg-white/70 backdrop-blur-sm px-5 py-6"
              >
                <div className="w-11 h-11 rounded-lg bg-orange-500/10 flex items-center justify-center mb-4">
                  <i className={`fas ${item.icon} text-orange-600`} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 tracking-wide mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action */}
      <section className="py-14 relative" style={{ background: HERO_GRADIENT }}>
        <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-2xl md:text-3xl font-medium text-white mb-3 tracking-tight">
            Start your event today
          </h2>
          <p className="text-sm md:text-base text-white/90 mb-6 max-w-xl mx-auto">
            Organizers across Kenya already ride with {BRAND_NAME} - list yours and own the night.
          </p>
          <Link
            to="/admin/events/create"
            className="inline-flex items-center gap-2 bg-white text-orange-700 px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-orange-50 transition-colors shadow-md"
          >
            Create event
            <i className="fas fa-arrow-right text-[10px]" />
          </Link>
        </div>
      </section>
      </SiteMain>
    </SiteLayout>
  );
};

export default HomePage;
